import React, { forwardRef, HTMLAttributes, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Box } from '../../new/core-components/box'
import { ThemeColors, ThemeShadows } from '../../new/theme'

type Props = {
  active: boolean
  backgroundColor?: ThemeColors
  boxShadow?: ThemeShadows
  checked: boolean
  disabled?: boolean
  hover: boolean
  hoverBackgroundColor?: ThemeColors
  hoverColor?: ThemeColors
  isCategoryControl?: boolean
  isCategoryLabel?: boolean
  isMultiSelect?: boolean
  isWithBottomDivider?: boolean
  onClick?: () => void
  sticky?: boolean
} & Omit<HTMLAttributes<HTMLLIElement>, 'onClick' | 'color'>

interface SearchItemManagerProps extends Props {
  onHoverActive: (fn: () => void) => void
}

const paddingY = '1rem'
const paddingX = '1.5rem'

const SearchItem = styled(Box)<Props>`
  transition: 0.1s;
  padding: ${paddingY} ${paddingX};
  cursor: ${({ isCategoryLabel, disabled }) => !isCategoryLabel && !disabled && 'pointer'};

  ${props =>
    props.sticky &&
    css`
      position: sticky;
      top: 0px;
      z-index: 1;
      background: ${props.theme.colors[props.backgroundColor || 'white']};
    `}

  ${({ checked, isMultiSelect, theme }) =>
    isMultiSelect &&
    css`
      background: ${checked ? theme.colors['bg/list-item-selected'] : 'bg/list-item-unselected'};
      box-shadow: ${checked ? theme.shadows['border/section-selected'] : undefined} !important;
    `}

  ${({ isCategoryControl, theme }) =>
    isCategoryControl &&
    css`
      background: ${theme.colors['bg/list-item-selected']};
    `}
  

  ${({ hoverBackgroundColor, sticky, hover, theme }) =>
    sticky &&
    hover &&
    css`
      background: ${hoverBackgroundColor ? theme.colors[hoverBackgroundColor] : undefined};
    `}

  ${({ theme, disabled, isCategoryLabel, backgroundColor, sticky }) =>
    !disabled &&
    !isCategoryLabel &&
    !sticky &&
    css`
      background-color: ${backgroundColor ? theme.colors[backgroundColor] : undefined};
    `}

  ${props =>
    props.disabled &&
    css`
      span {
        color: ${props => props.theme.colors['text/disabled']};
      }
    `}

  ${props =>
    props.hidden &&
    css`
      display: none;
    `}

  &:focus {
    outline: none;
  }

  ${props =>
    props.hover &&
    !props.sticky &&
    css`
      background: ${props => props.theme.colors['bg/list-item-hover']};
    `}
`

export const SearchItemManager = forwardRef<HTMLLIElement, SearchItemManagerProps>(
  ({ onHoverActive, ...props }, ref) => {
    const { hover, onClick } = props
    useEffect(() => {
      if (hover && onClick) {
        onHoverActive(onClick)
      }
    }, [hover])

    return <SearchItem as='li' ref={ref} {...props} />
  }
)

SearchItemManager.displayName = 'SearchItemManager'
